import Image from 'next/image';
import React, { memo, useCallback } from 'react';
interface ResizeHandleProps {
  direction: string;
  onMouseDown: (e: React.MouseEvent | React.TouchEvent, direction: string) => void;
  allowAdjust: boolean;
  isMaximized: boolean;
}
interface HandleConfig {
  style: React.CSSProperties;
  ariaLabel: string;
  isImage?: boolean;
}
const ResizeHandle: React.FC<ResizeHandleProps> = ({
  direction,
  onMouseDown,
  allowAdjust,
  isMaximized
}) => {
  const handleMouseDown = useCallback((e: React.MouseEvent | React.TouchEvent) => onMouseDown(e, direction), [direction, onMouseDown]);
  const getHandleConfig = useCallback((dir: string): HandleConfig | null => {
    const configs: Record<string, HandleConfig> = {
      'top-left': {
        style: {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '27px',
          height: '27px',
          cursor: allowAdjust ? 'nw-resize' : 'default',
          zIndex: 3
        },
        ariaLabel: 'Resize from top left'
      },
      'top-right': {
        style: {
          position: 'absolute',
          right: '0',
          top: '0',
          width: '14px',
          height: '27px',
          cursor: allowAdjust ? 'ne-resize' : 'default',
          zIndex: 3
        },
        ariaLabel: 'Resize from top right'
      },
      'bottom-left': {
        style: {
          position: 'absolute',
          left: '0',
          bottom: '0',
          width: '27px',
          height: '27px',
          cursor: allowAdjust ? 'sw-resize' : 'default',
          zIndex: 3
        },
        ariaLabel: 'Resize from bottom left'
      },
      'bottom-right': {
        style: {
          height: '25px',
          width: '25px',
          position: 'absolute',
          display: allowAdjust ? 'block' : 'none',
          right: '0',
          bottom: '0',
          zIndex: '4'
        },
        ariaLabel: 'Resize from bottom right',
        isImage: true
      },
      top: {
        style: {
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          height: '7px',
          cursor: allowAdjust ? 'ns-resize' : 'default',
          zIndex: 2
        },
        ariaLabel: 'Resize from top'
      },
      bottom: {
        style: {
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          height: '7px',
          cursor: allowAdjust ? 'ns-resize' : 'default',
          zIndex: 2,
          marginRight: '5px',
          marginLeft: '5px'
        },
        ariaLabel: 'Resize from bottom'
      },
      left: {
        style: {
          position: 'absolute',
          left: '0',
          top: '0',
          bottom: '0',
          width: '7px',
          cursor: allowAdjust ? 'ew-resize' : 'default',
          zIndex: 2,
          marginBottom: '5px'
        },
        ariaLabel: 'Resize from left'
      },
      right: {
        style: {
          position: 'absolute',
          right: '0',
          top: '0',
          bottom: '0',
          width: '7px',
          cursor: allowAdjust ? 'ew-resize' : 'default',
          zIndex: 2,
          marginBottom: '7px',
          display: isMaximized ? 'none' : 'block'
        },
        ariaLabel: 'Resize from right'
      }
    };
    return configs[dir] || null;
  }, [allowAdjust, isMaximized]);
  const renderBottomRightHandle = useCallback(() => <div style={{
    height: '25px',
    width: '25px',
    position: 'absolute',
    display: allowAdjust ? 'block' : 'none',
    right: '0',
    bottom: '0',
    zIndex: '4'
  }}>
        <Image onMouseDown={handleMouseDown} style={{
      right: '5',
      bottom: '5',
      position: 'absolute',
      cursor: allowAdjust ? 'se-resize' : 'default',
      zIndex: 5
    }} src="/images/resize_icon.svg" width={92} height={30} alt="TRS" className="absolute bottom-[6px] right-[5px] m-auto h-auto w-auto" />
      </div>, [handleMouseDown, allowAdjust]);
  const renderStandardHandle = useCallback((config: HandleConfig) => <button style={config.style} onMouseDown={handleMouseDown} aria-label={config.ariaLabel} />, [handleMouseDown]);
  const config = getHandleConfig(direction);
  if (!config) {
    return null;
  }
  if (direction === 'bottom-right') {
    return renderBottomRightHandle();
  }
  return renderStandardHandle(config);
};
export default memo(ResizeHandle);